/**
 * Owner Birthday Promotions — Phase 5A growth/retention.
 *
 * Mirrors /owner/promotions UX with the extra fields the birthday
 * model carries: `promotion_type` (free_drink / percentage / free_item /
 * custom), `terms`, and the canonical 7-day claim window which is
 * computed server-side based on the customer's locked DOB.
 *
 * Active vs. inactive is toggled via the dedicated activate /
 * deactivate endpoints (mirroring rewards UX), not via PATCH, because
 * the backend separates those concerns.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Cake, Edit, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import Drawer from '@/components/ui_primitives/Drawer';
import { FieldRow, Input, Textarea, Select } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';
import { confirm } from '@/components/ui_primitives/confirm';

const PROMOTION_TYPES = [
  { value: 'free_item', label: 'Free item' },
  { value: 'discount', label: 'Discount' },
  { value: 'voucher', label: 'Voucher' },
];

const schema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(500),
  promotion_type: z.enum(['free_item', 'discount', 'voucher']),
  terms: z.string().max(500).optional().or(z.literal('')),
  valid_from_date: z.string().optional().or(z.literal('')),
  valid_to_date: z.string().optional().or(z.literal('')),
  is_active: z.boolean().default(true),
}).superRefine((v, ctx) => {
  if (v.valid_from_date && v.valid_to_date && v.valid_from_date > v.valid_to_date) {
    ctx.addIssue({
      code: 'custom',
      path: ['valid_to_date'],
      message: 'End date must be on or after start date',
    });
  }
});

export default function OwnerBirthdayPromotionsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const promosQ = useQuery({
    queryKey: qk.ownerBirthdayPromotions(brandId),
    queryFn: () => api.ownerBirthdayPromotions(token),
    enabled: !!token && !!brandId,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: qk.ownerBirthdayPromotions(brandId) });

  const toggle = useMutation({
    mutationFn: ({ id, on }) =>
      on
        ? api.ownerActivateBirthdayPromotion(token, id)
        : api.ownerDeactivateBirthdayPromotion(token, id),
    onSuccess: () => { toast.success('Saved'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });

  const remove = useMutation({
    mutationFn: (id) => api.ownerDeleteBirthdayPromotion(token, id),
    onSuccess: () => { toast.success('Birthday promotion deleted'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Delete failed')),
  });

  if (promosQ.isLoading) return <FullSpinner />;
  if (promosQ.error) {
    return <QueryError error={promosQ.error} onRetry={() => promosQ.refetch()} />;
  }
  const raw = promosQ.data || [];
  const items = Array.isArray(raw) ? raw : raw.items || [];

  return (
    <div className="space-y-6" data-testid="owner-birthday-promotions-page">
      <PageHeader
        kicker="Birthday Rewards"
        title="Surprise customers on their birthday"
        subtitle="Birthday rewards auto-unlock for 7 days around each customer's birthday and can be claimed once per year. Claim codes expire after 14 days."
        actions={
          <Button
            leftIcon={<Plus size={14} />}
            onClick={() => setEditing('new')}
            testID="bday-promo-add"
          >
            Add birthday reward
          </Button>
        }
      />

      <SectionCard
        title="How it works"
        subtitle="Customers lock their birthday once in the mobile app. From 3 days before to 3 days after their birthday, every active birthday reward from brands they have joined becomes claimable. Brand uniqueness enforced — only one active promotion per brand."
      />

      {items.length === 0 ? (
        <EmptyState
          icon={<Cake size={20} />}
          title="No birthday rewards yet"
          subtitle="Create your first birthday reward — customers love being recognised on their special day."
          action={
            <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>
              Create birthday reward
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-[#1A1A1A] border border-white/8 rounded-2xl overflow-hidden flex flex-col"
              data-testid={`bday-promo-card-${p.id}`}
            >
              <div className="relative aspect-[3/1] bg-black/40">
                {p.image_url ? (
                  <img
                    src={resolveImageUrl(p.image_url)}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-3 left-3">
                  {p.is_active ? (
                    <StatusPill variant="gold">Active</StatusPill>
                  ) : (
                    <StatusPill variant="slate">Inactive</StatusPill>
                  )}
                </div>
                <div className="absolute top-3 right-3">
                  <StatusPill variant="slate">
                    {PROMOTION_TYPES.find((t) => t.value === p.promotion_type)
                      ?.label ?? p.promotion_type}
                  </StatusPill>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="font-display font-semibold text-base text-white truncate">
                  {p.title}
                </div>
                <p className="text-xs text-white/55 line-clamp-2">{p.description}</p>
                {p.terms ? (
                  <p className="text-[11px] text-white/35 italic line-clamp-2">
                    Terms: {p.terms}
                  </p>
                ) : null}
                {typeof p.total_claims === 'number' ? (
                  <div className="text-[11px] text-white/45 mt-1">
                    {p.total_claims} claims · {p.total_used ?? 0} used
                  </div>
                ) : null}
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="subtle"
                    leftIcon={<Edit size={12} />}
                    onClick={() => setEditing(p)}
                    testID={`bday-promo-edit-${p.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Power size={12} />}
                    onClick={() => toggle.mutate({ id: p.id, on: !p.is_active })}
                    testID={`bday-promo-toggle-${p.id}`}
                  >
                    {p.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    leftIcon={<Trash2 size={12} />}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete "${p.title}"?`,
                        message: 'Existing customer claims keep their codes, but no new claims can be issued.',
                        confirmText: 'Delete',
                        tone: 'danger',
                      });
                      if (ok) remove.mutate(p.id);
                    }}
                    testID={`bday-promo-delete-${p.id}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <BirthdayPromoForm
          promo={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate(); setEditing(null); }}
        />
      ) : null}
    </div>
  );
}

function BirthdayPromoForm({ promo, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const isEdit = !!promo;
  const [image, setImage] = useState(promo?.image_url ?? '');
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: promo?.title ?? '',
      description: promo?.description ?? '',
      promotion_type: promo?.promotion_type ?? 'free_drink',
      terms: promo?.terms ?? '',
      valid_from_date: promo?.valid_from_date ?? '',
      valid_to_date: promo?.valid_to_date ?? '',
      is_active: isEdit ? !!promo?.is_active : true,
    },
  });

  const onSubmit = async (vals) => {
    if (!image) {
      toast.error('Banner image is required');
      return;
    }
    try {
      const { is_active, ...rest } = vals;
      const body = {
        ...rest,
        image_url: image,
        terms: vals.terms || null,
        valid_from_date: vals.valid_from_date || null,
        valid_to_date: vals.valid_to_date || null,
      };
      let saved;
      if (isEdit) {
        saved = await api.ownerPatchBirthdayPromotion(token, promo.id, body);
        // Reconcile activation state independently — backend uses
        // dedicated activate/deactivate endpoints.
        if (is_active && !promo.is_active) {
          await api.ownerActivateBirthdayPromotion(token, promo.id);
        } else if (!is_active && promo.is_active) {
          await api.ownerDeactivateBirthdayPromotion(token, promo.id);
        }
        toast.success('Birthday reward updated');
      } else {
        saved = await api.ownerCreateBirthdayPromotion(token, body);
        // Backend creates promos as active by default. If the operator
        // explicitly chose "inactive", deactivate immediately.
        if (!is_active && saved?.id) {
          await api.ownerDeactivateBirthdayPromotion(token, saved.id);
        }
        toast.success('Birthday reward created');
      }
      onSaved();
    } catch (e) {
      const detail = String(e?.detail ?? '');
      if (detail.includes('brand_already_has_active_birthday_promotion')) {
        toast.error('You already have an active birthday reward. Deactivate it first.');
      } else {
        toast.error(detail || 'Save failed');
      }
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? 'Edit birthday reward' : 'New birthday reward'}
      subtitle="Birthday rewards unlock automatically for 7 days around each customer's locked DOB."
      testID="bday-promo-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            testID="bday-promo-save"
          >
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Banner image" required>
          <ImageUploader value={image} onChange={setImage} aspect="banner" />
        </FieldRow>
        <FieldRow label="Title" required error={errors.title?.message}>
          <Input
            {...register('title')}
            placeholder="e.g. Birthday Coffee — On Us"
            testID="bday-promo-title"
          />
        </FieldRow>
        <FieldRow label="Description" required error={errors.description?.message}>
          <Textarea
            rows={3}
            {...register('description')}
            placeholder="What does the customer get? Keep it short and clear."
          />
        </FieldRow>
        <FieldRow
          label="Reward type"
          required
          error={errors.promotion_type?.message}
          hint="Used to categorise rewards in analytics and the customer app."
        >
          <Select {...register('promotion_type')}>
            {PROMOTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </FieldRow>
        <FieldRow
          label="Terms & conditions"
          hint="Optional. e.g. 'One per customer per year. Not valid with other promos.'"
          error={errors.terms?.message}
        >
          <Textarea rows={2} {...register('terms')} />
        </FieldRow>

        <div className="grid grid-cols-2 gap-4">
          <FieldRow
            label="Valid from"
            hint="Optional. Leave blank for always-on."
            error={errors.valid_from_date?.message}
          >
            <Input
              type="date"
              {...register('valid_from_date')}
              data-testid="bday-promo-valid-from"
            />
          </FieldRow>
          <FieldRow
            label="Valid until"
            hint="Optional."
            error={errors.valid_to_date?.message}
          >
            <Input
              type="date"
              {...register('valid_to_date')}
              data-testid="bday-promo-valid-to"
            />
          </FieldRow>
        </div>

        <FieldRow label="Status">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="accent-gold w-4 h-4"
              data-testid="bday-promo-active"
              {...register('is_active')}
            />
            <span className="text-sm text-white">
              {isEdit ? 'Active' : 'Activate immediately'}
            </span>
          </label>
        </FieldRow>
      </form>
    </Drawer>
  );
}
